// ============================================================================
// LOGO REGISTRY - Single Source of Truth for all brand logos
// ============================================================================

export type LogoEntry = {
  key: string;                    // normalized key (e.g., "walmart", "amazon")
  displayName: string;            // human-readable name
  type: "merchant" | "purchase";  // merchant = seller, purchase = gift card brand
  localPath: string;              // e.g., "/brands/merchant/walmart.svg"
  sourceUrl: string;              // official source used to obtain logo
  format: "svg" | "png";
  checksum?: string;              // sha256 of stored file
  lastVerifiedAt?: string;        // ISO timestamp of last verification
  lastUpdatedAt?: string;         // ISO timestamp (only when file changes)
  notes?: string;                 // optional notes about the logo
};

// ============================================================================
// LOGO REGISTRY - All brand logos with official source URLs
// ============================================================================

export const LOGO_REGISTRY: Record<string, LogoEntry> = {
  // ========== MERCHANTS ==========
  
  // Electronics
  "apple": {
    key: "apple",
    displayName: "Apple",
    type: "merchant",
    localPath: "/brands/merchant/apple.svg",
    sourceUrl: "/wi/assets/logos/imported/apple-logo.png",
    format: "svg",
    notes: "Apple logo from newsroom"
  },
  "best-buy": {
    key: "best-buy",
    displayName: "Best Buy",
    type: "merchant",
    localPath: "/brands/merchant/best-buy.svg",
    sourceUrl: "https://www.bestbuy.ca/",
    format: "svg",
  },
  "cineplex": {
    key: "cineplex",
    displayName: "Cineplex",
    type: "merchant",
    localPath: "/brands/merchant/cineplex.svg",
    sourceUrl: "https://www.cineplex.com/",
    format: "svg",
  },
  "the-source": {
    key: "the-source",
    displayName: "The Source",
    type: "merchant",
    localPath: "/brands/merchant/the-source.svg",
    sourceUrl: "https://www.thesource.ca/",
    format: "svg",
  },
  "staples": {
    key: "staples",
    displayName: "Staples",
    type: "merchant",
    localPath: "/brands/merchant/staples.svg",
    sourceUrl: "https://www.staples.ca/",
    format: "svg",
  },

  // Grocery
  "farm-boy": {
    key: "farm-boy",
    displayName: "Farm Boy",
    type: "merchant",
    localPath: "/brands/merchant/farm-boy.svg",
    sourceUrl: "https://www.farmboy.ca/",
    format: "svg",
  },
  "food-basics": {
    key: "food-basics",
    displayName: "Food Basics",
    type: "merchant",
    localPath: "/brands/merchant/food-basics.svg",
    sourceUrl: "https://www.foodbasics.ca/",
    format: "svg",
  },
  "foodland": {
    key: "foodland",
    displayName: "Foodland",
    type: "merchant",
    localPath: "/brands/merchant/foodland.svg",
    sourceUrl: "https://www.foodland.ca/",
    format: "svg",
  },
  "fortinos": {
    key: "fortinos",
    displayName: "Fortinos",
    type: "merchant",
    localPath: "/brands/merchant/fortinos.svg",
    sourceUrl: "https://www.fortinos.ca/",
    format: "svg",
  },
  "freshco": {
    key: "freshco",
    displayName: "FreshCo",
    type: "merchant",
    localPath: "/brands/merchant/freshco.svg",
    sourceUrl: "https://www.freshco.com/",
    format: "svg",
  },
  "independent-grocer": {
    key: "independent-grocer",
    displayName: "Independent Grocer",
    type: "merchant",
    localPath: "/brands/merchant/independent-grocer.svg",
    sourceUrl: "https://www.yourindependentgrocer.ca/",
    format: "svg",
  },
  "loblaws": {
    key: "loblaws",
    displayName: "Loblaws",
    type: "merchant",
    localPath: "/brands/merchant/loblaws.svg",
    sourceUrl: "https://www.loblaws.ca/",
    format: "svg",
  },
  "longos": {
    key: "longos",
    displayName: "Longo's",
    type: "merchant",
    localPath: "/brands/merchant/longos.svg",
    sourceUrl: "https://www.longos.com/",
    format: "svg",
  },
  "metro": {
    key: "metro",
    displayName: "Metro",
    type: "merchant",
    localPath: "/brands/merchant/metro.svg",
    sourceUrl: "https://www.metro.ca/",
    format: "svg",
  },
  "no-frills": {
    key: "no-frills",
    displayName: "No Frills",
    type: "merchant",
    localPath: "/brands/merchant/no-frills.svg",
    sourceUrl: "https://www.nofrills.ca/",
    format: "svg",
  },
  "real-canadian-superstore": {
    key: "real-canadian-superstore",
    displayName: "Real Canadian Superstore",
    type: "merchant",
    localPath: "/brands/merchant/real-canadian-superstore.svg",
    sourceUrl: "https://www.realcanadiansuperstore.ca/",
    format: "svg",
  },
  "rexall": {
    key: "rexall",
    displayName: "Rexall",
    type: "merchant",
    localPath: "/brands/merchant/rexall.svg",
    sourceUrl: "https://www.rexall.ca/",
    format: "svg",
  },
  "shoppers-drug-mart": {
    key: "shoppers-drug-mart",
    displayName: "Shoppers Drug Mart",
    type: "merchant",
    localPath: "/brands/merchant/shoppers-drug-mart.svg",
    sourceUrl: "https://www.shoppersdrugmart.ca/",
    format: "svg",
  },
  "sobeys": {
    key: "sobeys",
    displayName: "Sobeys",
    type: "merchant",
    localPath: "/brands/merchant/sobeys.svg",
    sourceUrl: "https://www.sobeys.com/",
    format: "svg",
  },
  "valu-mart": {
    key: "valu-mart",
    displayName: "Valu-mart",
    type: "merchant",
    localPath: "/brands/merchant/valu-mart.svg",
    sourceUrl: "https://www.valumart.ca/",
    format: "svg",
  },
  "zehrs": {
    key: "zehrs",
    displayName: "Zehrs",
    type: "merchant",
    localPath: "/brands/merchant/zehrs.svg",
    sourceUrl: "https://www.zehrs.ca/",
    format: "svg",
  },

  // Home Improvement
  "home-depot": {
    key: "home-depot",
    displayName: "Home Depot",
    type: "merchant",
    localPath: "/brands/merchant/home-depot.svg",
    sourceUrl: "https://www.homedepot.ca/",
    format: "svg",
  },
  "home-hardware": {
    key: "home-hardware",
    displayName: "Home Hardware",
    type: "merchant",
    localPath: "/brands/merchant/home-hardware.svg",
    sourceUrl: "https://www.homehardware.ca/",
    format: "svg",
  },
  "ikea": {
    key: "ikea",
    displayName: "IKEA",
    type: "merchant",
    localPath: "/brands/merchant/ikea.svg",
    sourceUrl: "https://www.ikea.com/ca/en/",
    format: "svg",
  },
  "rona": {
    key: "rona",
    displayName: "RONA",
    type: "merchant",
    localPath: "/brands/merchant/rona.svg",
    sourceUrl: "https://www.rona.ca/",
    format: "svg",
  },

  // Restaurants
  "a-and-w": {
    key: "a-and-w",
    displayName: "A&W",
    type: "merchant",
    localPath: "/brands/merchant/a-and-w.svg",
    sourceUrl: "https://web.aw.ca/",
    format: "svg",
  },
  "boston-pizza": {
    key: "boston-pizza",
    displayName: "Boston Pizza",
    type: "merchant",
    localPath: "/brands/merchant/boston-pizza.svg",
    sourceUrl: "https://bostonpizza.com/",
    format: "svg",
  },
  "burger-king": {
    key: "burger-king",
    displayName: "Burger King",
    type: "merchant",
    localPath: "/brands/merchant/burger-king.svg",
    sourceUrl: "https://www.burgerking.ca/",
    format: "svg",
  },
  "doordash": {
    key: "doordash",
    displayName: "DoorDash",
    type: "merchant",
    localPath: "/brands/merchant/doordash.svg",
    sourceUrl: "https://www.doordash.com/",
    format: "svg",
  },
  "earls": {
    key: "earls",
    displayName: "Earls",
    type: "merchant",
    localPath: "/brands/merchant/earls.svg",
    sourceUrl: "https://earls.ca/",
    format: "svg",
  },
  "freshii": {
    key: "freshii",
    displayName: "Freshii",
    type: "merchant",
    localPath: "/brands/merchant/freshii.svg",
    sourceUrl: "https://www.freshii.com/",
    format: "svg",
  },
  "milestones": {
    key: "milestones",
    displayName: "Milestones",
    type: "merchant",
    localPath: "/brands/merchant/milestones.svg",
    sourceUrl: "https://www.milestonesrestaurants.com/",
    format: "svg",
  },
  "pita-pit": {
    key: "pita-pit",
    displayName: "Pita Pit",
    type: "merchant",
    localPath: "/brands/merchant/pita-pit.svg",
    sourceUrl: "https://pitapit.ca/",
    format: "svg",
  },
  "quesada": {
    key: "quesada",
    displayName: "Quesada",
    type: "merchant",
    localPath: "/brands/merchant/quesada.svg",
    sourceUrl: "https://www.quesada.ca/",
    format: "svg",
  },
  "red-lobster": {
    key: "red-lobster",
    displayName: "Red Lobster",
    type: "merchant",
    localPath: "/brands/merchant/red-lobster.svg",
    sourceUrl: "https://www.redlobster.ca/",
    format: "svg",
  },
  "skip-the-dishes": {
    key: "skip-the-dishes",
    displayName: "SkipTheDishes",
    type: "merchant",
    localPath: "/brands/merchant/skip-the-dishes.svg",
    sourceUrl: "https://www.skipthedishes.com/",
    format: "svg",
  },
  "starbucks": {
    key: "starbucks",
    displayName: "Starbucks",
    type: "merchant",
    localPath: "/brands/merchant/starbucks.svg",
    sourceUrl: "https://www.starbucks.ca/",
    format: "svg",
  },
  "subway": {
    key: "subway",
    displayName: "Subway",
    type: "merchant",
    localPath: "/brands/merchant/subway.svg",
    sourceUrl: "https://www.subway.com/en-CA",
    format: "svg",
  },
  "the-keg": {
    key: "the-keg",
    displayName: "The Keg",
    type: "merchant",
    localPath: "/brands/merchant/the-keg.svg",
    sourceUrl: "https://www.thekeg.com/",
    format: "svg",
  },
  "tim-hortons": {
    key: "tim-hortons",
    displayName: "Tim Hortons",
    type: "merchant",
    localPath: "/brands/merchant/tim-hortons.svg",
    sourceUrl: "https://www.timhortons.ca/",
    format: "svg",
  },

  // Shopping
  "adidas": {
    key: "adidas",
    displayName: "Adidas",
    type: "merchant",
    localPath: "/brands/merchant/adidas.svg",
    sourceUrl: "https://www.adidas.ca/",
    format: "svg",
  },
  "american-eagle": {
    key: "american-eagle",
    displayName: "American Eagle",
    type: "merchant",
    localPath: "/brands/merchant/american-eagle.svg",
    sourceUrl: "https://www.ae.com/ca/en",
    format: "svg",
  },
  "ardene": {
    key: "ardene",
    displayName: "Ardene",
    type: "merchant",
    localPath: "/brands/merchant/ardene.svg",
    sourceUrl: "https://www.ardene.com/ca/en",
    format: "svg",
  },
  "athleta": {
    key: "athleta",
    displayName: "Athleta",
    type: "merchant",
    localPath: "/brands/merchant/athleta.svg",
    sourceUrl: "https://athleta.gapcanada.ca/",
    format: "svg",
  },
  "banana-republic": {
    key: "banana-republic",
    displayName: "Banana Republic",
    type: "merchant",
    localPath: "/brands/merchant/banana-republic.svg",
    sourceUrl: "https://bananarepublic.gapcanada.ca/",
    format: "svg",
  },
  "canadian-tire": {
    key: "canadian-tire",
    displayName: "Canadian Tire",
    type: "merchant",
    localPath: "/brands/merchant/canadian-tire.svg",
    sourceUrl: "https://www.canadiantire.ca/",
    format: "svg",
  },
  "costco": {
    key: "costco",
    displayName: "Costco",
    type: "merchant",
    localPath: "/brands/merchant/costco.svg",
    sourceUrl: "https://www.costco.ca/",
    format: "svg",
  },
  "dollarama": {
    key: "dollarama",
    displayName: "Dollarama",
    type: "merchant",
    localPath: "/brands/merchant/dollarama.svg",
    sourceUrl: "https://www.dollarama.com/",
    format: "svg",
  },
  "foot-locker": {
    key: "foot-locker",
    displayName: "Foot Locker",
    type: "merchant",
    localPath: "/brands/merchant/foot-locker.svg",
    sourceUrl: "https://www.footlocker.ca/",
    format: "svg",
  },
  "gap": {
    key: "gap",
    displayName: "Gap",
    type: "merchant",
    localPath: "/brands/merchant/gap.svg",
    sourceUrl: "https://www.gapcanada.ca/",
    format: "svg",
  },
  "giant-tiger": {
    key: "giant-tiger",
    displayName: "Giant Tiger",
    type: "merchant",
    localPath: "/brands/merchant/giant-tiger.svg",
    sourceUrl: "https://www.gianttiger.com/",
    format: "svg",
  },
  "h-and-m": {
    key: "h-and-m",
    displayName: "H&M",
    type: "merchant",
    localPath: "/brands/merchant/h-and-m.svg",
    sourceUrl: "https://www2.hm.com/en_ca/index.html",
    format: "svg",
  },
  "homesense": {
    key: "homesense",
    displayName: "HomeSense",
    type: "merchant",
    localPath: "/brands/merchant/homesense.svg",
    sourceUrl: "https://www.homesense.ca/",
    format: "svg",
  },
  "hudsons-bay": {
    key: "hudsons-bay",
    displayName: "Hudson's Bay",
    type: "merchant",
    localPath: "/brands/merchant/hudsons-bay.svg",
    sourceUrl: "https://www.thebay.com/",
    format: "svg",
  },
  "indigo": {
    key: "indigo",
    displayName: "Indigo",
    type: "merchant",
    localPath: "/brands/merchant/indigo.svg",
    sourceUrl: "https://www.indigo.ca/",
    format: "svg",
  },
  "marshalls": {
    key: "marshalls",
    displayName: "Marshalls",
    type: "merchant",
    localPath: "/brands/merchant/marshalls.svg",
    sourceUrl: "https://www.marshalls.ca/",
    format: "svg",
  },
  "marks": {
    key: "marks",
    displayName: "Mark's",
    type: "merchant",
    localPath: "/brands/merchant/marks.svg",
    sourceUrl: "https://www.marks.com/",
    format: "svg",
  },
  "old-navy": {
    key: "old-navy",
    displayName: "Old Navy",
    type: "merchant",
    localPath: "/brands/merchant/old-navy.svg",
    sourceUrl: "https://oldnavy.gapcanada.ca/",
    format: "svg",
  },
  "petsmart": {
    key: "petsmart",
    displayName: "PetSmart",
    type: "merchant",
    localPath: "/brands/merchant/petsmart.svg",
    sourceUrl: "https://www.petsmart.ca/",
    format: "svg",
  },
  "pro-hockey-life": {
    key: "pro-hockey-life",
    displayName: "Pro Hockey Life",
    type: "merchant",
    localPath: "/brands/merchant/pro-hockey-life.svg",
    sourceUrl: "https://www.prohockeylife.com/",
    format: "svg",
  },
  "roots": {
    key: "roots",
    displayName: "Roots",
    type: "merchant",
    localPath: "/brands/merchant/roots.svg",
    sourceUrl: "https://www.roots.com/ca/en/",
    format: "svg",
  },
  "saks-off-5th": {
    key: "saks-off-5th",
    displayName: "Saks OFF 5th",
    type: "merchant",
    localPath: "/brands/merchant/saks-off-5th.svg",
    sourceUrl: "https://www.saksoff5th.com/",
    format: "svg",
  },
  "sport-chek": {
    key: "sport-chek",
    displayName: "Sport Chek",
    type: "merchant",
    localPath: "/brands/merchant/sport-chek.svg",
    sourceUrl: "https://www.sportchek.ca/",
    format: "svg",
  },
  "toys-r-us": {
    key: "toys-r-us",
    displayName: 'Toys "R" Us',
    type: "merchant",
    localPath: "/brands/merchant/toys-r-us.svg",
    sourceUrl: "https://www.toysrus.ca/",
    format: "svg",
  },
  "walmart": {
    key: "walmart",
    displayName: "Walmart",
    type: "merchant",
    localPath: "/brands/merchant/walmart.svg",
    sourceUrl: "https://www.walmart.ca/",
    format: "svg",
    lastUpdatedAt: "2026-01-03",
  },
  "winners": {
    key: "winners",
    displayName: "Winners",
    type: "merchant",
    localPath: "/brands/merchant/winners.svg",
    sourceUrl: "https://www.winners.ca/",
    format: "svg",
  },
  "lululemon": {
    key: "lululemon",
    displayName: "lululemon",
    type: "merchant",
    localPath: "/brands/merchant/lululemon.svg",
    sourceUrl: "https://shop.lululemon.com/",
    format: "svg",
  },

  // Specialty
  "bath-and-body-works": {
    key: "bath-and-body-works",
    displayName: "Bath & Body Works",
    type: "merchant",
    localPath: "/brands/merchant/bath-and-body-works.svg",
    sourceUrl: "https://www.bathandbodyworks.ca/",
    format: "svg",
  },
  "sephora": {
    key: "sephora",
    displayName: "Sephora",
    type: "merchant",
    localPath: "/brands/merchant/sephora.svg",
    sourceUrl: "https://www.sephora.com/ca/en/",
    format: "svg",
  },

  // Travel
  "air-canada": {
    key: "air-canada",
    displayName: "Air Canada",
    type: "merchant",
    localPath: "/brands/merchant/air-canada.svg",
    sourceUrl: "https://www.aircanada.com/",
    format: "svg",
  },
  "airbnb": {
    key: "airbnb",
    displayName: "Airbnb",
    type: "merchant",
    localPath: "/brands/merchant/airbnb.svg",
    sourceUrl: "https://www.airbnb.ca/",
    format: "svg",
  },
  "esso": {
    key: "esso",
    displayName: "Esso",
    type: "merchant",
    localPath: "/brands/merchant/esso.svg",
    sourceUrl: "https://www.esso.ca/",
    format: "svg",
  },
  "petro-canada": {
    key: "petro-canada",
    displayName: "Petro-Canada",
    type: "merchant",
    localPath: "/brands/merchant/petro-canada.svg",
    sourceUrl: "https://www.petro-canada.ca/",
    format: "svg",
  },
  "shell": {
    key: "shell",
    displayName: "Shell",
    type: "merchant",
    localPath: "/brands/merchant/shell.svg",
    sourceUrl: "https://www.shell.ca/",
    format: "svg",
  },
  "uber": {
    key: "uber",
    displayName: "Uber",
    type: "merchant",
    localPath: "/brands/merchant/uber.svg",
    sourceUrl: "https://www.uber.com/ca/en/",
    format: "svg",
  },
  "uber-eats": {
    key: "uber-eats",
    displayName: "Uber Eats",
    type: "merchant",
    localPath: "/brands/merchant/uber-eats.svg",
    sourceUrl: "https://www.ubereats.com/ca",
    format: "svg",
  },
  "via-rail": {
    key: "via-rail",
    displayName: "VIA Rail",
    type: "merchant",
    localPath: "/brands/merchant/via-rail.svg",
    sourceUrl: "https://www.viarail.ca/",
    format: "svg",
  },
  "westjet": {
    key: "westjet",
    displayName: "WestJet",
    type: "merchant",
    localPath: "/brands/merchant/westjet.svg",
    sourceUrl: "https://www.westjet.com/",
    format: "svg",
  },

  // ========== PURCHASE BRANDS (Gift Card Brands) ==========
  
  "amazon": {
    key: "amazon",
    displayName: "Amazon",
    type: "purchase",
    localPath: "/brands/purchase/amazon.svg",
    sourceUrl: "https://www.amazon.ca/",
    format: "svg",
  },
  "apple-gc": {
    key: "apple-gc",
    displayName: "Apple Gift Card",
    type: "purchase",
    localPath: "/brands/purchase/apple.svg",
    sourceUrl: "https://www.apple.com/ca/shop/gift-cards",
    format: "svg",
  },
  "disney-plus": {
    key: "disney-plus",
    displayName: "Disney+",
    type: "purchase",
    localPath: "/brands/purchase/disney-plus.svg",
    sourceUrl: "https://www.disneyplus.com/",
    format: "svg",
  },
  "google-play": {
    key: "google-play",
    displayName: "Google Play",
    type: "purchase",
    localPath: "/brands/purchase/google-play.svg",
    sourceUrl: "https://play.google.com/",
    format: "svg",
  },
  "netflix": {
    key: "netflix",
    displayName: "Netflix",
    type: "purchase",
    localPath: "/brands/purchase/netflix.svg",
    sourceUrl: "https://www.netflix.com/",
    format: "svg",
  },
  "nintendo-eshop": {
    key: "nintendo-eshop",
    displayName: "Nintendo eShop",
    type: "purchase",
    localPath: "/brands/purchase/nintendo-eshop.svg",
    sourceUrl: "https://www.nintendo.com/",
    format: "svg",
    lastUpdatedAt: "2026-01-03",
  },
  "petro-canada-purchase": {
    key: "petro-canada-purchase",
    displayName: "Petro-Canada",
    type: "purchase",
    localPath: "/brands/purchase/petro-canada.svg",
    sourceUrl: "https://www.petro-canada.ca/",
    format: "svg",
    lastUpdatedAt: "2026-01-03",
  },
  "playstation": {
    key: "playstation",
    displayName: "PlayStation Store",
    type: "purchase",
    localPath: "/brands/purchase/playstation.svg",
    sourceUrl: "https://store.playstation.com/",
    format: "svg",
    lastUpdatedAt: "2026-01-03",
  },
  "roblox": {
    key: "roblox",
    displayName: "Roblox",
    type: "purchase",
    localPath: "/brands/purchase/roblox.svg",
    sourceUrl: "https://www.roblox.com/",
    format: "svg",
    lastUpdatedAt: "2026-01-03",
  },
  "spotify": {
    key: "spotify",
    displayName: "Spotify",
    type: "purchase",
    localPath: "/brands/purchase/spotify.svg",
    sourceUrl: "https://www.spotify.com/",
    format: "svg",
  },
  "steam": {
    key: "steam",
    displayName: "Steam",
    type: "purchase",
    localPath: "/brands/purchase/steam.svg",
    sourceUrl: "https://store.steampowered.com/",
    format: "svg",
  },
  "vanilla-visa": {
    key: "vanilla-visa",
    displayName: "Vanilla Visa",
    type: "purchase",
    localPath: "/brands/purchase/vanilla-visa.svg",
    sourceUrl: "https://www.vanillagift.com/",
    format: "svg",
    lastUpdatedAt: "2026-01-03",
  },
  "xbox": {
    key: "xbox",
    displayName: "Xbox",
    type: "purchase",
    localPath: "/brands/purchase/xbox.svg",
    sourceUrl: "https://www.xbox.com/",
    format: "svg",
    lastUpdatedAt: "2026-01-03",
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Normalize a brand name to a registry key
 */
export function normalizeToRegistryKey(name: string): string {
  return name
    .toLowerCase()
    .replace(/®|™/g, "")
    .replace(/gift\s*card/gi, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Get logo entry by key (exact match)
 */
export function getLogoEntry(key: string): LogoEntry | null {
  return LOGO_REGISTRY[key] ?? null;
}

/**
 * Get logo entry by normalized name
 */
export function getLogoByName(name: string): LogoEntry | null {
  const key = normalizeToRegistryKey(name);
  return LOGO_REGISTRY[key] ?? null;
}

/**
 * Get local path for a logo by key
 */
export function getLogoPath(key: string): string | null {
  const entry = LOGO_REGISTRY[key];
  return entry?.localPath ?? null;
}

/**
 * Get local path for a merchant logo by name
 */
export function getMerchantLogoPath(name: string): string | null {
  const key = normalizeToRegistryKey(name);
  const entry = LOGO_REGISTRY[key];
  if (entry?.type === "merchant") {
    return entry.localPath;
  }
  return null;
}

/**
 * Get local path for a purchase brand logo by name
 */
export function getPurchaseBrandLogoPath(name: string): string | null {
  const key = normalizeToRegistryKey(name);
  
  // Try exact match first (purchase type only)
  let entry = LOGO_REGISTRY[key];
  if (entry?.type === "purchase") {
    return entry.localPath;
  }
  
  // Try common aliases for purchase brands
  const aliases: Record<string, string> = {
    "nintendo": "nintendo-eshop",
    "nintendo-eshop": "nintendo-eshop",
    "playstation-store": "playstation",
    "playstation": "playstation",
    "vanilla-visa-prepaid-card": "vanilla-visa",
    "vanilla-visa": "vanilla-visa",
    "apple": "apple-gc",
    "petro-canada": "petro-canada-purchase",
    "roblox": "roblox",
    "xbox": "xbox",
    "spotify": "spotify",
    "steam": "steam",
    "netflix": "netflix",
    "google-play": "google-play",
    "disney-plus": "disney-plus",
    "amazon": "amazon",
  };
  
  const aliasKey = aliases[key];
  if (aliasKey && aliasKey !== key) {
    entry = LOGO_REGISTRY[aliasKey];
    if (entry?.type === "purchase") {
      return entry.localPath;
    }
  }
  
  // Fallback: check if there's a merchant logo that could work for purchase display
  // (Some brands like Petro-Canada exist as both merchant and purchase)
  const merchantEntry = LOGO_REGISTRY[key];
  if (merchantEntry?.type === "merchant") {
    // Check if there's a corresponding purchase version with -purchase suffix
    const purchaseKey = `${key}-purchase`;
    const purchaseEntry = LOGO_REGISTRY[purchaseKey];
    if (purchaseEntry?.type === "purchase") {
      return purchaseEntry.localPath;
    }
  }
  
  return null;
}

/**
 * Get all logos of a specific type
 */
export function getLogosByType(type: "merchant" | "purchase"): LogoEntry[] {
  return Object.values(LOGO_REGISTRY).filter(entry => entry.type === type);
}

/**
 * Get all logos that need verification (never verified or not verified this year)
 */
export function getLogosNeedingVerification(): LogoEntry[] {
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  
  return Object.values(LOGO_REGISTRY).filter(entry => {
    if (!entry.lastVerifiedAt) return true;
    const verifiedDate = new Date(entry.lastVerifiedAt);
    return verifiedDate < oneYearAgo;
  });
}
